import { Component, OnInit } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { PortalService, Portal, PortalAccess } from '@kelyn-technologies/dsio-angular-lib/dsio-api';
import { AuthorizationRequestService } from '@kelyn-technologies/dsio-angular-lib';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { TenantCleanupService } from './tenant-detail/tenant-cleanup.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'dsio-internal-admin-client';
  public isLoggedIn = false;
  portals: Portal[] = [];
  public tenantId: string | null = sessionStorage.getItem("tenantId");
  environment: any = environment;
  public firstName: String|undefined = '';
  portalsLoading = false;
  isImpersonating = false;
  isTenantDetailPage = false;
  deleting = false;
  portalAccessInFlight: Record<string, boolean> = {};
  portalAccessStatus: string | null = null;
  portalAccessStatusPortalId: string | null = null;
  private portalStatusTimeout: ReturnType<typeof setTimeout> | null = null;
  stripeSyncInFlight = false;

  constructor(
    private readonly keycloak: KeycloakService,
    private portalService: PortalService,
    private route: ActivatedRoute,
    private router: Router,
    private readonly authorizationRequestService: AuthorizationRequestService,
    private snackBar: MatSnackBar,
    private http: HttpClient,
    private tenantCleanupService: TenantCleanupService
  ) {}

  public async ngOnInit() {

    this.isLoggedIn = this.keycloak.isLoggedIn();

    if (!this.isLoggedIn) {
      this.keycloak.login()
      this.isLoggedIn = this.keycloak.isLoggedIn();
    } else {
      const userProfile: KeycloakProfile = await this.keycloak.loadUserProfile();
      this.firstName = userProfile.firstName;
    }

    this.route.queryParamMap.subscribe(queryParam => {
      const queryParamTenant = queryParam.get('tenantId');
      this.tenantId = queryParamTenant;

      if (queryParamTenant) {
        this.tenantId = queryParamTenant;
        sessionStorage.setItem('tenantId', queryParamTenant);
      } else {
        sessionStorage.removeItem('tenantId');
      }

      this.isImpersonating = !!this.tenantId;

      if (this.isImpersonating) {
        this.loadPortals();
      } else {
        this.portals = [];
        this.portalsLoading = false;
      }
    });

    this.syncTenantDetailRoute(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncTenantDetailRoute(event.urlAfterRedirects);
      }
    });
  }

  get showTenantContext(): boolean {
    return this.isImpersonating && this.isTenantDetailPage;
  }

  private loadPortals(): void {
    if (!this.isImpersonating) {
      this.portals = [];
      return;
    }

    this.portalsLoading = true;

    this.portalService.getPortalsByTenant(this.tenantId!).subscribe({
      next: (response) => {
        const list = response.portals ?? [];
        this.portals = list;
        this.portalsLoading = false;
      },
      error: () => {
        this.portals = [];
        this.portalsLoading = false;
      }
    });
  }

  public login() {
    this.keycloak.login();
  }

  public logout() {
    this.keycloak.logout();
  }

  openPortal(url: string): void {
    let fullUrl = url;
    if (!url.startsWith('http')) {
      fullUrl = `https://${url}`;
    }
    window.open(fullUrl, '_blank');
  }

  handlePortalClick(portal: Portal): void {
    const portalId = portal.portal_id;
    const tenantId = this.tenantId;

    if (!this.isImpersonating || !tenantId || !portalId) {
      this.showMessage('Missing tenant or portal ID.');
      return;
    }

    if (this.portalAccessInFlight[portalId]) {
      this.showMessage('Portal access is already in progress.');
      return;
    }

    this.portalAccessInFlight[portalId] = true;
    this.setPortalStatus('Checking portal access...', portalId);

    this.resolvePortalAccessByTenant(portalId, tenantId)
      .subscribe({
        next: (result) => {
          this.handlePortalAuthorizationResult(result, tenantId);
        },
        error: (err: HttpErrorResponse) => {
          if (err?.status === 401) {
            this.showMessage('Missing authorized tenant.');
          } else if (err?.status === 403) {
            this.showMessage('Access denied for this portal.');
          } else {
            this.showMessage('Failed to get portal access. Please try again.');
          }
          this.setPortalStatus('Portal access failed.', portalId);
        },
        complete: () => {
          this.portalAccessInFlight[portalId] = false;
        }
      });
  }

  private handlePortalAuthorizationResult(
    result: PortalAccessOutcome,
    tenantId: string
  ): void {
    switch (result.kind) {
      case "navigate":
        this.setPortalStatus('Opening portal...', this.portalAccessStatusPortalId ?? undefined, 3000);
        this.openPortal(result.url);
        return;
      case "needs_authorization":
        this.setPortalStatus('Authorization required. Redirecting...', result.portalId);
        this.router.navigate(['/authorizations'], {
          queryParams: {
            tenantId,
            portalId: result.portalId,
            requestId: result.requestId,
          },
        });
        return;
      case "deny":
        this.showMessage('No applicable grants for this portal.');
        this.setPortalStatus('No applicable grants for this portal.', this.portalAccessStatusPortalId ?? undefined);
        return;
    }
  }

  formatType(type: string): string {
    switch (type) {
      case 'aws': return 'AWS Account';
      case 'commvault': return 'Commvault Account';
      default: return type;
    }
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }

  triggerStripeSync(dryRun = false): void {
    if (this.stripeSyncInFlight) {
      return;
    }

    this.stripeSyncInFlight = true;
    const suffix = dryRun ? '?dry_run=true' : '';
    this.http
      .post<{ processed?: number; skipped?: number }>(
        `${environment.API_URL}/stripe/sync${suffix}`,
        {},
      )
      .pipe(
        finalize(() => {
          this.stripeSyncInFlight = false;
        })
      )
      .subscribe({
        next: (response) => {
          const processed = response?.processed ?? 0;
          const skipped = response?.skipped ?? 0;
          const label = dryRun ? 'Dry run' : 'Sync';
          this.showMessage(`${label} complete: processed ${processed}, skipped ${skipped}.`);
        },
        error: () => {
          this.showMessage('Stripe sync failed.');
        },
      });
  }

  confirmAndDelete(): void {
    if (!this.tenantId) {
      this.showMessage('Missing tenant id');
      return;
    }

    const confirmed = window.confirm(
      `Start cleanup & delete tenant '${this.tenantId}'? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    this.deleting = true;
    this.tenantCleanupService.deleteTenant(this.tenantId).subscribe({
      next: () => {
        this.showMessage('Tenant cleanup initiated');
        this.deleting = false;
        this.router.navigate(['/tenants']);
      },
      error: (err) => {
        const msg = err?.error?.error || err?.message || err?.statusText || 'Delete failed';
        this.showMessage(msg);
        this.deleting = false;
      },
    });
  }

  private resolvePortalAccessByTenant(
    portalId: string,
    tenantId: string
  ): Observable<PortalAccessOutcome> {
    const fetchAccess = () =>
      this.portalService.getPortalAccessByTenant(portalId, tenantId);
    return fetchAccess().pipe(
      switchMap((access) =>
        this.handlePortalAccess(portalId, access, fetchAccess)
      ),
      finalize(() => {
        this.portalAccessInFlight[portalId] = false;
      })
    );
  }

  private handlePortalAccess(
    portalId: string,
    access: PortalAccess,
    fetchAccess: () => Observable<PortalAccess>
  ): Observable<PortalAccessOutcome> {
    switch (access.action) {
      case "navigate":
        return this.toNavigateResult(access);
      case "request_access": {
        this.setPortalStatus('Requesting portal access...', portalId);
        const grantId = access.target?.trim();
        if (!grantId) {
          return throwError(() => new Error("Grant ID is missing for this portal."));
        }
        return this.authorizationRequestService
          .createAuthorizationRequest(grantId)
          .pipe(
            switchMap((authorizationRequest) => {
              if (authorizationRequest.is_valid) {
                return fetchAccess().pipe(
                  switchMap((nextAccess) => {
                    if (nextAccess.action !== "navigate") {
                      return throwError(
                        () =>
                          new Error("Portal access still requires authorization.")
                      );
                    }
                    return this.toNavigateResult(nextAccess);
                  })
                );
              }

              const requestId = authorizationRequest.id?.trim();
              if (!requestId) {
                return throwError(
                  () => new Error("Authorization request ID is missing.")
                );
              }
              const outcome: PortalAccessOutcome = {
                kind: "needs_authorization",
                portalId,
                requestId,
              };
              return of(outcome);
            })
          );
      }
      case "update_access": {
        this.setPortalStatus('Authorization required. Review request.', portalId);
        const requestId = access.target?.trim();
        if (!requestId) {
          return throwError(
            () => new Error("Authorization request ID is missing.")
          );
        }
        const outcome: PortalAccessOutcome = {
          kind: "needs_authorization",
          portalId,
          requestId,
        };
        return of(outcome);
      }
      case "deny":
        this.setPortalStatus('No applicable grants for this portal.', portalId);
        return of({ kind: "deny" });
      default:
        return throwError(
          () => new Error("Unknown portal access action received.")
        );
    }
  }

  private toNavigateResult(access: PortalAccess): Observable<PortalAccessOutcome> {
    const url = this.resolvePortalUrl(access);
    if (!url) {
      return throwError(
        () => new Error("Navigate action returned without a URL.")
      );
    }
    return of({ kind: "navigate", url });
  }

  private resolvePortalUrl(access: PortalAccess): string | null {
    const targetUrl = access.target?.trim();
    const dataUrl = (access.data as { url?: string } | undefined)?.url?.trim();
    return targetUrl || dataUrl || null;
  }

  isPortalBusy(portal: Portal): boolean {
    const portalId = portal.portal_id;
    return !!portalId && !!this.portalAccessInFlight[portalId];
  }

  private setPortalStatus(message: string, portalId?: string, clearAfterMs?: number): void {
    this.portalAccessStatus = message;
    this.portalAccessStatusPortalId = portalId ?? null;
    this.showMessage(message);
    if (this.portalStatusTimeout) {
      clearTimeout(this.portalStatusTimeout);
      this.portalStatusTimeout = null;
    }
    if (clearAfterMs && clearAfterMs > 0) {
      this.portalStatusTimeout = setTimeout(() => {
        this.portalAccessStatus = null;
        this.portalAccessStatusPortalId = null;
        this.portalStatusTimeout = null;
      }, clearAfterMs);
    }
  }

  private syncTenantDetailRoute(url: string): void {
    this.isTenantDetailPage = url.split('?')[0] === '/tenant-detail';
  }
}

type PortalAccessOutcome =
  | { kind: "navigate"; url: string }
  | { kind: "needs_authorization"; requestId: string; portalId: string }
  | { kind: "deny" };
